import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { useMemo } from "react";
import { useBannerSlideCarousel } from "../../hooks/useBannerSlideCarousel";

const slideLayerSxBase = {
  position: "absolute",
  left: 0,
  right: 0,
  top: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "wrap",
  rowGap: 0.5,
  columnGap: 0.5,
  width: "100%",
};

const carouselNavIconButtonSx = {
  flexShrink: 0,
  color: "inherit",
  p: 0.25,
  "&:hover": {
    color: "inherit",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
  },
};

/**
 * Reusable horizontal banner carousel: any number of slides, optional auto-advance,
 * and per-slide entry side when moving forward ("next").
 *
 * @param {object} props
 * @param {import("react").ReactNode[]} props.slides
 * @param {("right" | "left")[]} [props.slideEntrySideForNext] — per index; falls back to defaultSlideEntrySideForNext
 * @param {"right" | "left"} [props.defaultSlideEntrySideForNext="right"]
 * @param {number | undefined} props.autoAdvanceMs
 * @param {boolean} props.enabled
 * @param {string} props.ariaLabelPrevious
 * @param {string} props.ariaLabelNext
 * @param {number} [props.transitionMs=350]
 * @param {string} [props.viewportMinHeight="2.75rem"]
 * @param {string[]} [props.slideKeys] — stable React keys per slide
 */
const BannerMessageCarousel = ({
  slides,
  slideEntrySideForNext = [],
  defaultSlideEntrySideForNext = "right",
  autoAdvanceMs,
  enabled,
  ariaLabelPrevious,
  ariaLabelNext,
  transitionMs = 350,
  viewportMinHeight = "2.75rem",
  slideKeys,
}) => {
  const slideCount = slides.length;

  const getEntrySideForNext = useMemo(() => {
    return (slideIndex) =>
      slideEntrySideForNext[slideIndex] ?? defaultSlideEntrySideForNext;
  }, [defaultSlideEntrySideForNext, slideEntrySideForNext]);

  const {
    activeIndex,
    goNext,
    goPrev,
    suppressTransition,
    xPercentBySlide,
    transitionCss,
  } = useBannerSlideCarousel({
    slideCount,
    getEntrySideForNext,
    autoAdvanceMs,
    enabled,
    transitionMs,
  });

  if (slideCount === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        maxWidth: "100%",
        columnGap: 1,
      }}
    >
      <IconButton
        type="button"
        size="small"
        aria-label={ariaLabelPrevious}
        onClick={goPrev}
        sx={carouselNavIconButtonSx}
      >
        <ChevronLeft sx={{ fontSize: 18, color: "#fff !important" }} />
      </IconButton>
      <Box
        aria-live="polite"
        sx={{
          overflow: "hidden",
          flex: "1 1 auto",
          minWidth: 0,
          maxWidth: { xs: "100%", md: "40rem" },
          position: "relative",
          minHeight: viewportMinHeight,
        }}
      >
        {slides.map((node, index) => (
          <Box
            key={slideKeys?.[index] ?? `banner-slide-${String(index)}`}
            sx={{
              ...slideLayerSxBase,
              zIndex: activeIndex === index ? 2 : 1,
              transform: `translate3d(${xPercentBySlide[index] ?? 0}%, -50%, 0)`,
              transition: suppressTransition ? "none" : transitionCss,
            }}
          >
            {node}
          </Box>
        ))}
      </Box>
      <IconButton
        type="button"
        size="small"
        aria-label={ariaLabelNext}
        onClick={goNext}
        sx={carouselNavIconButtonSx}
      >
        <ChevronRight sx={{ fontSize: 18, color: "#fff !important" }} />
      </IconButton>
    </Box>
  );
};

export default BannerMessageCarousel;
